/**
 * RibbonConfigHelper
 *
 * Maps the study-level ribbon configuration payload (`filteredlist` / the
 * `getConfiguration` block of a stored answer) onto the ~40 component flags that
 * drive the ribbon UI: data format, sample-size list, indexing, nesting,
 * transpose, significance testing, export permissions, low-base / threshold
 * identifiers, custom content and aggregated-variable options.
 *
 * Extracted verbatim from chatbotexpand.component.ts (`this.` -> `comp.`);
 * behavior is unchanged. No bare `this` existed in this cluster.
 */
export class RibbonConfigHelper {

  /**
   * Computes the confidence-level list and transpose-button availability directly from the
   * study-level ribbon config (comp.filteredlist). These are study-scoped (not per-question),
   * so deriving them here keeps them stable regardless of navigation. Without this, navigating
   * from a snapshot question to a BA/CT question could leave the global is_confidenceLevel /
   * is_TransposeBtn empty, hiding the Confidence Level dropdown and Transpose button.
   */
  static computeStudyRibbonFlags(comp: any): { confidenceLevels: any[]; transposeAvailable: boolean } {
    const cfg: any = comp.filteredlist;
    const activeDataFormat = cfg?.dataformat?.find((df: any) => df.value === true);
    const dataFormatKey = activeDataFormat ? activeDataFormat.key : (cfg?.dataformat?.[0]?.key || 'Missing');
    const result = {
      confidenceLevels: cfg?.confidence_level || [],
      transposeAvailable: dataFormatKey === 'Respondent Level' ? (cfg?.output_page?.[0]?.value ?? false) : false
    };
    return result;
  }

  static LoadRibbonData(comp: any, receivedData: any) {
    let ribbonSource = receivedData;
    // When loading from history, receivedData is the full analyticalData object. 
    // The ribbon configuration data is nested inside getConfiguration.
    if (receivedData && !receivedData.comparison_point && receivedData.getConfiguration) {
      ribbonSource = Array.isArray(receivedData.getConfiguration) ? receivedData.getConfiguration[0] : receivedData.getConfiguration;
    }

    const activeDataFormat = ribbonSource.dataformat?.find((df: any) => df.value === true);
    const dataFormatKey = activeDataFormat ? activeDataFormat.key : (ribbonSource.dataformat ? ribbonSource.dataformat[0]?.key : 'Missing');
    comp.selectedDataFormat = dataFormatKey;
    comp._configService.isRespondentLevel = dataFormatKey === 'Respondent Level';
    comp._configService.isAggregatedData = dataFormatKey === 'Aggregated';
    comp._configService.isSemiAggregatedData = dataFormatKey === 'Semi-Aggregated';

    if (comp.SampleSizeList.length == 0 && ribbonSource && ribbonSource.comparison_point) {
      for (const item of ribbonSource.comparison_point) {
        if (item.value) {
          comp.SampleSizeList.push(item);
        }
      }
    } else if (comp.SampleSizeList.length == 0) {
      console.warn(`[RIBBON-DEBUG] Cannot populate SampleSizeList. comparison_point is missing!`);
    }
    const outputPage = ribbonSource.output_page || [];
    comp.is_indexing = (comp.mcode === 2 || comp.mcode === 3) ? true : (outputPage[1]?.value || false);
    comp.isRowNesting =
      dataFormatKey === 'Respondent Level'
        ? ribbonSource.nesting[0]?.is_selected
        : false;
    comp.isColumnNesting = ribbonSource.nesting ? ribbonSource.nesting[1]?.is_selected : false;
    comp.is_TransposeBtn =
      dataFormatKey === 'Respondent Level'
        ? ribbonSource.output_page[0]?.value
        : false;
    comp.filteringAnsBlock =
      dataFormatKey === 'Respondent Level'
        ? ribbonSource.filter_function[0]?.value
        : false;
    comp.AnsBaseBlock =
      dataFormatKey === 'Respondent Level'
        ? ribbonSource.filter_function[1]?.value
        : false;
    comp.is_sigtesting =
      dataFormatKey === 'Respondent Level'
        ? ribbonSource.output_page[2]?.value
        : false;
    (comp.is_brand_analysis = ribbonSource.is_brand_analysis),
      (comp.is_chart_visualization = ribbonSource.is_chart_visualization),
      (comp.is_sticky_selection = ribbonSource.is_sticky_selection),
      (comp.is_reset_selection = ribbonSource.is_reset_selection),
      (comp.is_Transpose = ribbonSource.output_page ? ribbonSource.output_page[0]?.value : false),
      (comp.is_sigtesting = (comp.mcode === 2 || comp.mcode === 3) ? true : (outputPage[2]?.value || false)),
      (comp.is_ranking = ribbonSource.output_page ? ribbonSource.output_page[3]?.value : false),
      (comp.is_confidenceLevel = receivedData?.confidence_level || ribbonSource?.confidence_level);
    (comp.isFilterToggle = ribbonSource.filter_function ? ribbonSource.filter_function[0]?.value : false),
      (comp.isAnswerToggle = ribbonSource.filter_function ? ribbonSource.filter_function[1]?.value : false),
      (comp.excelExport = ribbonSource.export_download ? ribbonSource.export_download[0]?.value : false),
      (comp.repository = ribbonSource.export_download ? ribbonSource.export_download[2]?.value : false),
      (comp.lowBaseIdentifier = ribbonSource.export_download ? ribbonSource.export_download[3]?.value : false),
      (comp.is_AI_Integration = ribbonSource.is_AI_Integration),
      (comp.is_StoryBoard = ribbonSource.is_StoryBoard),
      (comp.is_Column_Row_Percentage = ribbonSource.is_Column_Row_Percentage),
      (comp.insufficentBase = ribbonSource.low_base_identifier ? ribbonSource.low_base_identifier[0]?.data_value : 0),
      (comp.insufficientBase = ribbonSource.low_base_identifier ? ribbonSource.low_base_identifier[0]?.data_value : 0),
      (comp.lowBase = ribbonSource.low_base_identifier ? ribbonSource.low_base_identifier[1]?.data_value : 0),
      (comp.LowBase = ribbonSource.low_base_identifier ? ribbonSource.low_base_identifier[1]?.data_value : 0),
      (comp.threshold = ribbonSource.threshold_identifier ? ribbonSource.threshold_identifier[0]?.data_value : 0),
      (comp.iscustomgrp = ribbonSource.custom_content ? ribbonSource.custom_content[0]?.value : false),
      (comp.iscustomCalc = ribbonSource.custom_content ? ribbonSource.custom_content[1]?.value : false),
      (comp.iscustomreport = ribbonSource.custom_content ? ribbonSource.custom_content[2]?.value : false),
      (comp.changeEntryPoint = ribbonSource.save_selection ? ribbonSource.save_selection[1]?.value : false),
      (comp.aggregatedVariables = ribbonSource.aggregatedvariables),
      (comp.isWeightedBase =
        ribbonSource.aggregatedvariables && ribbonSource.aggregatedvariables[1]?.data_value == '' ? false : true),
      (comp.isSampleSize =
        ribbonSource.aggregatedvariables && ribbonSource.aggregatedvariables[2]?.data_value == '' ? false : true),
      (comp.mandatoryVariableMapping = ribbonSource.mandatory_variable_mapping),
      (comp.isSelectAllMetrics = ribbonSource.is_SelectAll_Metrics);
    comp.isSelectAllMetricsLevel = ribbonSource.isSelectAllMetrics;
    comp.SampleSizeContainer =
      dataFormatKey === 'Respondent Level'
        ? true
        : ribbonSource.isWeightedBase || ribbonSource.isSampleSize
          ? true
          : false;
    comp.isSampleSizeBlock =
      dataFormatKey === 'Respondent Level'
        ? true
        : ribbonSource.isSampleSize;
    comp.isWeightedSampleBlock =
      dataFormatKey === 'Respondent Level'
        ? true
        : ribbonSource.isWeightedBase;
    const noneexists = comp.SampleSizeList.some(
      (item: { key: string }) => item.key === 'None'
    );
    if (!noneexists) {
      let newPair = { key: 'None', value: true };
      comp.SampleSizeList.unshift(newPair);
    }
  }
}
